import {
    type BTFileSystem,
    type FileViewOptions,
} from "legacyComponents/FileUploadContainer.types";
import {
    getOwnerOptions,
    getShowOptions,
    getSubsOptions,
} from "legacyComponents/FileUploadContainer.utils";

import { BTLoginTypes, FileStatus, MediaType } from "types/enum";
import { type BTViewingPermissions } from "types/filePermissions";

import { type AttachmentItems } from "commonComponents/entity/media/common/MediaContextMenu/Overlays/AttachmentOverlay";
import { type SharingSettingsAPI } from "commonComponents/entity/sharing/SharingContainer/SharingContainer.types";

import { FileOptions } from "handlers";

import { isPDFFileType, isPhotoFileType } from "entity/media/common/media.utils";

export function areContextItemsPresent(items: Record<string, boolean>) {
    return Object.values(items).some((item) => item);
}

export function getAttachmentOverlayItems(
    file: BTFileSystem,
    isReadOnly: boolean,
    loginType?: BTLoginTypes,
    sharingSettings?: SharingSettingsAPI,
    hideNotify?: boolean,
    hideDraw?: boolean,
    hideEditOnline?: boolean,
    fileViewOptions?: FileViewOptions,
    isOnExternalPage?: boolean,
    nonDeletableFileIds?: number[],
    isInWebview?: boolean,
    showRenameMenuItem?: boolean,
): Record<AttachmentItems, boolean> {
    const isNonDeletable = nonDeletableFileIds?.includes(file.id) ?? false;
    return {
        download: showDownload(file, isOnExternalPage, isInWebview),
        openInNewTab: showOpenInNewTab(file, isInWebview),
        showOwners: showClientViewingPermissions(file, isReadOnly, fileViewOptions, loginType),
        showSubs: showSubViewingPermissions(file, isReadOnly, fileViewOptions, loginType),
        notifyOwners: hideNotify
            ? false
            : showNotifyOwners(file, isReadOnly, loginType, sharingSettings),
        notifySubs: hideNotify
            ? false
            : showNotifySubs(file, isReadOnly, loginType, sharingSettings),
        draw: hideDraw ? false : showDraw(file, isReadOnly, loginType),
        editOnline: hideEditOnline ? false : showEditOnline(file, isReadOnly, loginType),
        trash: isNonDeletable ? false : showTrash(file, isReadOnly, loginType),
        setAsMainPhoto: showSetAsMainPhoto(file, fileViewOptions),
        rename: showRenameMenuItem
            ? showRename(file, isReadOnly, loginType, isOnExternalPage)
            : false,
    };
}

export function showRename(
    file: BTFileSystem,
    isReadOnly: boolean,
    loginType?: BTLoginTypes,
    isOnExternalPage?: boolean,
) {
    return (
        !isReadOnly &&
        !isOnExternalPage &&
        !file.isTempFile &&
        loginType === BTLoginTypes.BUILDER &&
        !!file.permissions?.canEdit
    );
}

export function showDownload(
    file: BTFileSystem,
    isOnExternalPage?: boolean,
    isInWebview?: boolean,
) {
    return (
        !isInWebview &&
        !file.isTempFile &&
        !(file.mediaType === MediaType.Video && file.fileStatus !== FileStatus.Available) &&
        !(file.mediaType === MediaType.Video && isOnExternalPage)
    );
}

export function getOpenInNewTabLink(file: BTFileSystem) {
    if (file.mediaType === MediaType.Photo && file.annotatedDocPath) {
        return file.annotatedDocPath;
    }

    return file.docPath;
}

export function showOpenInNewTab(file: BTFileSystem, isInWebview?: boolean) {
    if (isInWebview || file.isTempFile) {
        return false;
    }

    if (file.mediaType === MediaType.Video && file.fileStatus === FileStatus.Available) {
        return true;
    }

    return (
        getOpenInNewTabLink(file) !== undefined &&
        (isPDFFileType(file.extension) || isPhotoFileType(file.extension))
    );
}

function canEditViewingPermissions(
    isReadOnly: boolean,
    loginType?: BTLoginTypes,
    viewingPermissions?: BTViewingPermissions,
) {
    return !isReadOnly && loginType === BTLoginTypes.BUILDER && viewingPermissions !== undefined;
}

function isVideoError(file: BTFileSystem) {
    return file.mediaType === MediaType.Video && file.fileStatus === FileStatus.Error;
}

export function showSubViewingPermissions(
    file: BTFileSystem,
    isReadOnly: boolean,
    fileViewOptions?: FileViewOptions,
    loginType?: BTLoginTypes,
) {
    const showOptions = getShowOptions(fileViewOptions);
    const subOptions = getSubsOptions(showOptions);
    return (
        canEditViewingPermissions(isReadOnly, loginType, file.viewingPermissions) &&
        !isVideoError(file) &&
        subOptions !== undefined &&
        subOptions.canEdit &&
        subOptions.visible
    );
}

export function showClientViewingPermissions(
    file: BTFileSystem,
    shouldHide: boolean,
    newFileOptions?: FileViewOptions,
    loginType?: BTLoginTypes,
) {
    const showOptions = getShowOptions(newFileOptions);
    const clientOptions = getOwnerOptions(showOptions);
    return (
        canEditViewingPermissions(shouldHide, loginType, file.viewingPermissions) &&
        !isVideoError(file) &&
        clientOptions !== undefined &&
        clientOptions.canEdit &&
        clientOptions.visible
    );
}

export function showNotifyOwners(
    file: BTFileSystem,
    isReadOnly: boolean,
    loginType?: BTLoginTypes,
    sharingSettings?: SharingSettingsAPI,
) {
    return (
        !isReadOnly &&
        loginType === BTLoginTypes.BUILDER &&
        file.viewingPermissions?.showOwner === true &&
        sharingSettings?.ownerPreference?.share === true
    );
}

export function showNotifySubs(
    file: BTFileSystem,
    isReadOnly: boolean,
    loginType?: BTLoginTypes,
    sharingSettings?: SharingSettingsAPI,
) {
    return (
        !isReadOnly &&
        loginType === BTLoginTypes.BUILDER &&
        file.viewingPermissions?.showSubs === true &&
        sharingSettings?.subVendorPreference?.share === true
    );
}

export function showAnnotate(file: BTFileSystem, isReadOnly: boolean, loginType?: BTLoginTypes) {
    return (
        !file.isTempFile &&
        !!file.permissions?.canAnnotate &&
        canUserDrawOrAnnotate(loginType) &&
        !isReadOnly &&
        isPDFFileType(file.extension)
    );
}

export function showViewAnnotations(
    file: BTFileSystem,
    isReadOnly: boolean,
    loginType?: BTLoginTypes,
) {
    return (
        file.hasAnnotations &&
        !file.isTempFile &&
        loginType !== undefined &&
        isPDFFileType(file.extension) &&
        (!file.permissions?.canAnnotate || isReadOnly)
    );
}

export function showDraw(file: BTFileSystem, isReadOnly: boolean, loginType?: BTLoginTypes) {
    return (
        !file.isTempFile &&
        !!file.permissions?.canDrawOnline &&
        canUserDrawOrAnnotate(loginType) &&
        !isReadOnly &&
        file.mediaType === MediaType.Photo
    );
}

function canUserDrawOrAnnotate(loginType?: BTLoginTypes) {
    return loginType === BTLoginTypes.BUILDER || loginType === BTLoginTypes.SUBS;
}

export function showEditOnline(file: BTFileSystem, isReadOnly: boolean, loginType?: BTLoginTypes) {
    return (
        !!file.permissions?.canEditOnline &&
        !file.isTempFile &&
        !file.isMacroEnabledExcelFile &&
        loginType !== undefined &&
        !isReadOnly
    );
}

export function showTrash(file: BTFileSystem, isReadOnly: boolean, loginType?: BTLoginTypes) {
    return !isReadOnly && loginType !== undefined && !!file.permissions?.canDelete;
}

export function showSetAsMainPhoto(file: BTFileSystem, fileViewOptions?: FileViewOptions) {
    return (
        (file.mediaType === MediaType.Photo &&
            !file.viewingPermissions?.mainFile &&
            fileViewOptions?.options
                ?.find((x) => x.title === "")
                ?.options?.find((y) => y.fileOptionKey === FileOptions.MainFile)?.canEdit) ??
        false
    );
}
